/**
 * UnifiedFormField.tsx
 *
 * Central component that routes field rendering to the appropriate field component
 * based on field type. This is the single source of truth for form field rendering.
 *
 * Security: Enforces field type restrictions based on rendering mode
 * Performance: Uses code splitting for heavy components (signature, camera, multi-image)
 */

import React, { Suspense } from 'react';
import {
  BaseFieldProps,
  FormFieldMode,
  FieldComponentMap,
  DEFAULT_SECURITY_CONFIG,
} from './shared/types';

// Import basic field components directly (no code splitting)
import { TextField } from './TextField';
import { NumberField } from './NumberField';
import { TextAreaField } from './TextAreaField';
import { DropdownField } from './DropdownField';
import { CheckboxField } from './CheckboxField';
import { CheckboxesField } from './CheckboxesField';
import { MultipleChoiceField } from './MultipleChoiceField';
import { InformationBoxField } from './InformationBoxField';
import { BarcodeScannerField } from './BarcodeScannerField';
import { FileUploadField } from './FileUploadField';
import { HiddenField } from './HiddenField';
import { PaymentField } from './PaymentField';

// Lazy load heavy components for better performance
const ImageUploadField = React.lazy(() =>
  import('./ImageUploadField').then(module => ({ default: module.ImageUploadField }))
);
const CameraUploadField = React.lazy(() =>
  import('./CameraUploadField').then(module => ({ default: module.CameraUploadField }))
);
const MultiImageCaptureField = React.lazy(() =>
  import('./MultiImageCaptureField').then(module => ({ default: module.MultiImageCaptureField }))
);
const SignaturePadField = React.lazy(() =>
  import('./SignaturePadField').then(module => ({ default: module.SignaturePadField }))
);

/**
 * Field component registry
 * Maps field type strings to their corresponding React components
 */
const FIELD_COMPONENT_MAP: FieldComponentMap = {
  // Text-based fields
  text: TextField,
  textField: TextField,
  email: TextField,
  url: TextField,
  date: TextField,

  // Number field
  number: NumberField,
  numberField: NumberField,

  // Text area fields
  textarea: TextAreaField,
  textArea: TextAreaField,
  'comment-box': TextAreaField,

  // Selection fields
  dropdown: DropdownField,
  select: DropdownField,
  checkboxes: CheckboxesField,
  'multiple-choice': CheckboxesField,

  // Boolean fields
  checkbox: CheckboxField,
  'yes-no-toggle': CheckboxField,
  yes_no: CheckboxField,

  // Information display
  'information-box': InformationBoxField,

  // File upload fields
  'file-upload': FileUploadField,
  image_upload: ImageUploadField as any, // Lazy loaded
  camera_upload: CameraUploadField as any, // Lazy loaded
  multi_image_capture: MultiImageCaptureField as any, // Lazy loaded

  // Signature field
  'signature-pad': SignaturePadField as any, // Lazy loaded

  // Barcode scanner
  'barcode-scanner': BarcodeScannerField,

  // Payment field
  payment: PaymentField,

  // Hidden field
  hidden: HiddenField,
};

/**
 * Fallback component for unsupported field types
 */
const UnsupportedFieldType: React.FC<{ fieldType: string }> = ({ fieldType }) => (
  <div className="p-4 border border-red-300 bg-red-50 rounded">
    <p className="text-sm text-red-600">
      Unsupported field type: <strong>{fieldType}</strong>
    </p>
    <p className="text-xs text-red-500 mt-1">
      This field type is not recognized by the system.
    </p>
  </div>
);

/**
 * Loading fallback for lazy-loaded components
 */
const FieldLoadingFallback: React.FC = () => (
  <div className="w-full h-48 border-2 border-dashed rounded-md bg-muted flex items-center justify-center">
    <p className="text-sm text-muted-foreground">Loading field...</p>
  </div>
);

interface UnifiedFormFieldProps extends Omit<BaseFieldProps, 'fieldConfig'> {
  /** Field configuration */
  fieldConfig: BaseFieldProps['fieldConfig'];

  /** Rendering mode */
  mode: FormFieldMode;

  /** Current field value */
  value: any;

  /** Change handler */
  onChange: (fieldId: string, value: any) => void;

  /** Validation errors */
  errors?: string[];

  /** Disabled state */
  disabled?: boolean;

  /** Error state */
  hasError?: boolean;

  /** Camera modal handler (for camera/image fields) */
  openCameraModal?: (field: any, promptId?: string) => void;

  /** All current form values - allows fields to reference other field values */
  formValues?: Record<string, any>;

  /** Payment modal handler (for payment fields) */
  openPaymentModal?: () => void;

  /** Server-resolved payment amount, major units. See BaseFieldProps. */
  preloadedAmount?: number;

  /** Additional class names */
  className?: string;

  /** Whether this field is part of a payment-enabled form (display-only mode) */
  isPaymentForm?: boolean;

  paymentStatus?: 'processing' | 'cancelled' | 'failed';
}

/**
 * UnifiedFormField Component
 *
 * Central renderer for all form fields with security and performance optimizations
 */
export const UnifiedFormField: React.FC<UnifiedFormFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value,
  onChange,
  errors = [],
  disabled = false,
  hasError = false,
  openCameraModal,
  formValues = {},
  openPaymentModal,
  className = '',
  isPaymentForm = false,
  paymentStatus,
  preloadedAmount,
}) => {
  // Security check: Validate field type is allowed for current mode
  if (mode === 'public' && !DEFAULT_SECURITY_CONFIG.publicAllowedTypes.includes(fieldConfig.type)) {
    console.warn(
      `[Security] Field type "${fieldConfig.type}" is not allowed in public mode. ` +
      `Field ID: ${fieldConfig.id}`
    );
    return (
      <div className="p-4 border border-yellow-300 bg-yellow-50 rounded">
        <p className="text-sm text-yellow-700">
          This field is not available in public mode.
        </p>
      </div>
    );
  }

  // Get the component for this field type
  const FieldComponent = FIELD_COMPONENT_MAP[fieldConfig.type];

  // Handle unknown field types
  if (!FieldComponent) {
    if (process.env.NODE_ENV === 'development') {
      console.error(
        `Unknown field type: "${fieldConfig.type}" for field ID: ${fieldConfig.id}. ` +
        `Available types: ${Object.keys(FIELD_COMPONENT_MAP).join(', ')}`
      );
    }
    return <UnsupportedFieldType fieldType={fieldConfig.type} />;
  }

  // Prepare props for the field component
  const fieldProps: BaseFieldProps = {
    fieldConfig,
    mode,
    value,
    onChange,
    errors,
    disabled,
    hasError,
    openCameraModal,
    formValues,
    openPaymentModal,
    paymentStatus,
    preloadedAmount,
    className,
    isPaymentForm,
  };

  // Render with Suspense for lazy-loaded components
  const isLazyComponent = [
    'image_upload',
    'camera_upload',
    'multi_image_capture',
    'signature-pad',
  ].includes(fieldConfig.type);

  if (isLazyComponent) {
    return (
      <Suspense fallback={<FieldLoadingFallback />}>
        <FieldComponent {...fieldProps} />
      </Suspense>
    );
  }

  // Render directly for non-lazy components
  return <FieldComponent {...fieldProps} />;
});

UnifiedFormField.displayName = 'UnifiedFormField';
